import { v4 as uuid } from 'uuid'
import {
  APP_SCROLL_SOURCES,
  AppScrollSource,
  WorkArea,
} from 'utilities/work-time-scroll'

const MAX_COUNT = 1_000_000
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
type ActivityType = 'INITIALIZED' | 'SCROLL' | 'WHEEL' | 'CLICK' | 'KEYDOWN'
export type DiagnosticState = 'ACTIVE' | 'IDLE' | 'HIDDEN' | 'DISABLED'
type EventType = 'scroll' | 'wheel' | 'click' | 'keydown' | 'touchstart'

// Metadata only. No keys, coordinates, DOM content, or URLs.
export class WorkTimeDiagnostics {
  private collectionUntil = 0
  private appScrollCounts = new Map<AppScrollSource, number>()
  private lastAppScrollType: AppScrollSource | null = null
  private lastAppScrollAt: number | null = null
  private lastScrollAreaType: WorkArea = 'UNKNOWN'
  private lastPointerInputAt: number | null = null
  private pointerDownCount = 0
  private pointerDragCount = 0
  private initializedAt: number
  private lastActivityType: ActivityType = 'INITIALIZED'
  private isTrusted = false
  private lastDirectInputAt: number | null = null
  private lastScrollAt: number | null = null
  private sequenceCount = 0
  private initializationCount = 0
  private acceptedEventCount = 0
  private trustedEventCount = 0
  private untrustedEventCount = 0
  private heartbeatFailureCount = 0
  private counts = { scroll: 0, wheel: 0, click: 0, keydown: 0, touchstart: 0 }

  constructor(
    private identity: {
      expectedCoachId: string
      tabId: string
      pageInstanceId: string
      navigationType: 'NAVIGATE' | 'RELOAD' | 'BACK_FORWARD' | 'UNKNOWN'
    },
    private pageStartedAt: number
  ) {
    this.initializedAt = pageStartedAt
  }

  initialize(now: number) {
    this.initializedAt = now
    this.initializationCount = Math.min(MAX_COUNT, this.initializationCount + 1)
    this.lastActivityType = 'INITIALIZED'
    this.isTrusted = false
  }

  observe(type: string, isTrusted: boolean, accepted: boolean, now: number) {
    if (!Object.prototype.hasOwnProperty.call(this.counts, type)) return
    const eventType = type as EventType
    this.counts[eventType] = Math.min(MAX_COUNT, this.counts[eventType] + 1)
    if (isTrusted)
      this.trustedEventCount = Math.min(MAX_COUNT, this.trustedEventCount + 1)
    else
      this.untrustedEventCount = Math.min(
        MAX_COUNT,
        this.untrustedEventCount + 1
      )
    if (type === 'scroll') this.lastScrollAt = now
    else if (isTrusted) this.lastDirectInputAt = now
    // Touch is diagnostic-only; it does not change the existing activity rules.
    if (accepted && type !== 'touchstart') {
      this.acceptedEventCount = Math.min(MAX_COUNT, this.acceptedEventCount + 1)
      this.lastActivityType = type.toUpperCase() as ActivityType
      this.isTrusted = isTrusted
    }
  }

  setCollectionUntil(until: number) {
    this.collectionUntil = until
  }

  // Stable identity deduplicates subscriptions from providers sharing a page.
  observeAppScroll = (source: AppScrollSource, now: number) => {
    if (now >= this.collectionUntil || !APP_SCROLL_SOURCES.includes(source))
      return
    this.appScrollCounts.set(
      source,
      Math.min(MAX_COUNT, (this.appScrollCounts.get(source) ?? 0) + 1)
    )
    this.lastAppScrollType = source
    this.lastAppScrollAt = now
  }

  observeScrollArea(area: WorkArea) {
    this.lastScrollAreaType = area
  }

  // Separate from legacy direct-input age, accepted events, and pay activity.
  observePointer(type: string, trusted: boolean, buttons: number, now: number) {
    if (now >= this.collectionUntil || !trusted) return
    if (type === 'pointerdown') {
      this.pointerDownCount = Math.min(MAX_COUNT, this.pointerDownCount + 1)
    } else if (type === 'pointermove' && buttons !== 0) {
      this.pointerDragCount = Math.min(MAX_COUNT, this.pointerDragCount + 1)
    } else return
    this.lastPointerInputAt = now
  }

  heartbeatFailed() {
    this.heartbeatFailureCount = Math.min(
      MAX_COUNT,
      this.heartbeatFailureCount + 1
    )
  }

  snapshot(
    now: number,
    lastActivityAt: number,
    isVisible: boolean,
    hasFocus: boolean,
    pageAreaType: WorkArea = 'UNKNOWN'
  ) {
    const age = (at: number | null) =>
      at === null ? null : Math.min(MAX_AGE_MS, Math.max(0, now - at))
    this.sequenceCount = Math.min(MAX_COUNT, this.sequenceCount + 1)
    return {
      ...this.identity,
      traceVersion: 2,
      pageAreaType,
      lastScrollAreaType: this.lastScrollAreaType,
      appScrollCounts: APP_SCROLL_SOURCES.map(source => ({
        source,
        count: this.appScrollCounts.get(source) ?? 0,
      })),
      lastAppScrollType: this.lastAppScrollType,
      lastAppScrollAgeMs: age(this.lastAppScrollAt),
      pointerDownCount: this.pointerDownCount,
      pointerDragCount: this.pointerDragCount,
      lastPointerInputAgeMs: age(this.lastPointerInputAt),
      eventId: uuid(),
      sequenceCount: this.sequenceCount,
      initializationCount: this.initializationCount,
      lastActivityType: this.lastActivityType,
      lastActivityAgeMs: age(lastActivityAt),
      lastDirectInputAgeMs: age(this.lastDirectInputAt),
      lastScrollAgeMs: age(this.lastScrollAt),
      initializedAgeMs: age(this.initializedAt),
      pageAgeMs: age(this.pageStartedAt),
      isTrusted: this.isTrusted,
      isVisible,
      hasFocus,
      scrollCount: this.counts.scroll,
      wheelCount: this.counts.wheel,
      clickCount: this.counts.click,
      keydownCount: this.counts.keydown,
      touchstartCount: this.counts.touchstart,
      trustedEventCount: this.trustedEventCount,
      untrustedEventCount: this.untrustedEventCount,
      acceptedEventCount: this.acceptedEventCount,
      heartbeatFailureCount: this.heartbeatFailureCount,
    }
  }
}

// Module lifetime = page lifetime. Remounts keep their page identity/counters;
// reloads get a new page ID but retain the tab ID where storage is available.
let page: { coachId: string; trace: WorkTimeDiagnostics } | undefined

// Keep the server-issued cutoff across reloads so auto-refresh loops have
// evidence on their first heartbeat too. This is only a collection hint:
// the server rechecks the flag before every log and can revoke it immediately.
export const readWorkDiagnosticsUntil = (coachId: string): number => {
  try {
    const until = Number(
      sessionStorage.getItem(`work-time-trace-until-${coachId}`)
    )
    return Number.isFinite(until) && until > Date.now() ? until : 0
  } catch {
    return 0
  }
}

export const saveWorkDiagnosticsUntil = (coachId: string, until: number) => {
  try {
    const key = `work-time-trace-until-${coachId}`
    if (until > Date.now()) sessionStorage.setItem(key, String(until))
    else sessionStorage.removeItem(key)
  } catch {
    // Without storage, the next reload needs a fresh server acknowledgement.
  }
}

export const getWorkTimeDiagnostics = (coachId: string) => {
  if (page?.coachId === coachId) return page.trace
  let tabId = uuid()
  try {
    const stored = sessionStorage.getItem('work-time-diagnostic-tab')
    if (stored && /^[0-9a-f-]{36}$/i.test(stored)) tabId = stored
    else sessionStorage.setItem('work-time-diagnostic-tab', tabId)
  } catch {
    // Storage-disabled browsers still have a per-page correlation ID.
  }
  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined
  const navigationType =
    navigation?.type === 'reload'
      ? 'RELOAD'
      : navigation?.type === 'back_forward'
      ? 'BACK_FORWARD'
      : navigation?.type === 'navigate'
      ? 'NAVIGATE'
      : 'UNKNOWN'
  const trace = new WorkTimeDiagnostics(
    { expectedCoachId: coachId, tabId, pageInstanceId: uuid(), navigationType },
    Date.now()
  )
  page = { coachId, trace }
  return trace
}
